/**
 * Supported configuration value types. These match the configuration types
 * available when authoring a configuration in Switcheroo / Statsig:
 * Boolean, Number, String, String list, Number list.
 */
export type DynamicConfigType = 'boolean' | 'number' | 'string' | 'stringList' | 'numberList';

export type DynamicConfig = {
	/**
	 * The Statsig dynamic config key (matches the key used when the
	 * configuration was created in Switcheroo).
	 */
	configKey: string;
	/**
	 * The value type stored in this dynamic config.
	 */
	type: DynamicConfigType;
};

export type DynamicConfigManifest = {
	[configName: string]: DynamicConfig;
};

/**
 * Manifest of all dynamic configurations consumed by WAC.
 *
 * Add new configurations here, then call `getDynamicConfig` with the
 * manifest entry to retrieve the value at runtime.
 * 
 * Example:
 *     testConfigBoolean: {
		configKey: "config-key",
		type: "boolean"
	}
 */
export const dynamicConfigManifest = {
	isPersonalizationEnabled: {
		configKey: 'wae-personalization-enabled',
		type: 'boolean',
	},
	routesRequiringAvailableProducts: {
		configKey: 'wac-routes-available-products',
		type: 'stringList',
	},
	routesRequiringUserProfile: {
		configKey: 'wac-routes-user-profile',
		type: 'stringList',
	},
	routesRequiringUserContext: {
		configKey: 'wac-routes-user-context',
		type: 'stringList',
	},
	routesRequiringLoggedOutUserContext: {
		configKey: 'wac-routes-logged-out-user-context',
		type: 'stringList',
	},
	dwellTimeEnabledRoutes: {
		configKey: 'dwell-time-enabled-routes',
		type: 'stringList',
	},
	routesAllowingSalesLeadsCta: {
		configKey: 'wac-sales-leads-cta-route-list',
		type: 'stringList',
	},
} as const satisfies DynamicConfigManifest;

/**
 * Union of all properties of `dynamicConfigManifest`. Used to type-narrow the
 * argument to `getDynamicConfig`.
 */
export type DynamicConfigManifestProperty =
	(typeof dynamicConfigManifest)[keyof typeof dynamicConfigManifest];
